import { ThemeProvider } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Hidden from "@mui/material/Hidden";
import Toolbar from "@mui/material/Toolbar";
import clsx from "clsx";
import { memo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectFuseCurrentLayoutConfig,
  selectNavbarTheme,
  selectToolbarTheme,
} from "app/store/fuse/settingsSlice";
import { selectFuseNavbar } from "app/store/fuse/navbarSlice";
import NotificationPanelToggleButton from "../../shared-components/notificationPanel/NotificationPanelToggleButton";
import NavigationSearch from "../../shared-components/NavigationSearch";
import NavbarToggleButton from "../../shared-components/NavbarToggleButton";
import UserMenu from "../../shared-components/UserMenu";
import SelectProgram from "../../shared-components/programSelection/SelectProgram";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Theme,
} from "@mui/material";
import { authDataService } from "src/app/services/data/auth.data.service";
import { UserRole } from "src/app/@types/enums/user.roles";
import React from "react";
import ThemeOverride from "./navbar/ThemeOverride";

interface ToolbarLayout1Props {
  className?: string;
}

interface ProgramTheme {
  background: string;
  text: string;
}

interface ProgramThemes {
  [key: string]: ProgramTheme;
}

function ToolbarLayout1(props: ToolbarLayout1Props): JSX.Element {
  const config = useSelector(selectFuseCurrentLayoutConfig);
  const navbar = useSelector(selectFuseNavbar);
  const toolbarTheme = useSelector(selectToolbarTheme);
  const dispatch = useDispatch();

  const [themeProgram, setThemeProgram] = useState<string>("summer");

  // Program theme definitions
  const programThemes: ProgramThemes = {
    summer: {
      background: "rgba(255, 140, 0, 0.95)",
      text: "rgba(255, 255, 255, 0.95)",
    },
    inschool: {
      background: "rgba(0, 128, 128, 0.95)",
      text: "rgba(255, 255, 255, 0.95)",
    },
    leadership: {
      background: "rgba(75, 0, 130, 0.95)",
      text: "rgba(255, 255, 255, 0.95)",
    },
  };

  // Initialize from localStorage if available
  useEffect(() => {
    if (authDataService.userRoleId === UserRole.Youth) {
      const savedTheme = localStorage.getItem("youthPortalTheme");
      if (savedTheme && programThemes[savedTheme]) {
        setThemeProgram(savedTheme);
      }
    }
  }, []);

  // Apply theme change when program selection changes
  useEffect(() => {
    if (authDataService.userRoleId === UserRole.Youth) {
      // Store theme in localStorage for persistence
      localStorage.setItem("youthPortalTheme", themeProgram);

      const newTheme: Theme = {
        ...toolbarTheme,
        palette: {
          ...toolbarTheme.palette,
          background: {
            ...toolbarTheme.palette.background,
            default: programThemes[themeProgram].background,
          },
          text: {
            ...toolbarTheme.palette.text,
            primary: programThemes[themeProgram].text,
          },
        },
      };

      dispatch({ type: "fuse/settings/toolbarTheme", payload: newTheme });
      dispatch({ type: "fuse/settings/navbarTheme", payload: newTheme });
    }
  }, [themeProgram, dispatch, toolbarTheme]);

  const handleThemeChange = (event: SelectChangeEvent<string>): void => {
    setThemeProgram(event.target.value);
  };

  return (
    <ThemeProvider theme={toolbarTheme}>
      {/* Add ThemeOverride for direct DOM manipulation */}
      {authDataService.userRoleId === UserRole.Youth && (
        <ThemeOverride currentTheme={themeProgram} />
      )}

      <AppBar
        id="fuse-toolbar"
        className={clsx("flex relative z-20 shadow-md", props.className)}
        color="default"
        position="static"
      >
        <Toolbar className="p-0 min-h-48 md:min-h-64">
          <div className="flex flex-1 px-16">
            {authDataService.userRoleId === UserRole.Youth && (
              <FormControl
                variant="outlined"
                size="small"
                sx={{ minWidth: 180, mr: 2 }}
              >
                <InputLabel>Program</InputLabel>
                <Select
                  value={themeProgram}
                  onChange={handleThemeChange}
                  label="Program"
                >
                  <MenuItem value="summer">
                    Summer Youth Enrollment Program 2025
                  </MenuItem>
                  <MenuItem value="inschool">
                    In-School Youth Program 2025
                  </MenuItem>
                  <MenuItem value="leadership">
                    Youth Leadership Program 2024
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {config.navbar.display && config.navbar.position === "left" && (
              <>
                <Hidden lgDown>
                  {(config.navbar.style === "style-3" ||
                    config.navbar.style === "style-3-dense") && (
                    <NavbarToggleButton className="w-40 h-40 p-0 mx-0" />
                  )}

                  {config.navbar.style === "style-1" && !navbar.open && (
                    <NavbarToggleButton className="w-40 h-40 p-0 mx-0" />
                  )}
                </Hidden>

                <Hidden lgUp>
                  <NavbarToggleButton className="w-40 h-40 p-0 mx-0 sm:mx-8" />
                </Hidden>
              </>
            )}

            <SelectProgram />
          </div>

          <div className="flex items-center px-8 h-full overflow-x-auto">
            <NavigationSearch />
            <NotificationPanelToggleButton />
            <UserMenu />
          </div>
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
}

export default memo(ToolbarLayout1);
